import { Decimal } from '../money.js';
import { KeelError } from '../errors.js';
import { requireFinite } from '../ledger/shared.js';
/** Australian super caps for FY 2025-26 (cents). Indexed annually — update each July. */
export const AU_SUPER_CAPS = {
    financialYear: '2025-26',
    concessionalCents: 3_000_000,
    nonConcessionalCents: 12_000_000,
    bringForwardCents: 36_000_000,
    transferBalanceCapCents: 200_000_000,
    sgRate: 0.12,
};
/** Minimum annual pension drawdown rate by age (account-based pensions). */
export const pensionMinimumDrawdownRate = (age) => {
    if (age < 65)
        return 0.04;
    if (age < 75)
        return 0.05;
    if (age < 80)
        return 0.06;
    if (age < 85)
        return 0.07;
    if (age < 90)
        return 0.09;
    if (age < 95)
        return 0.11;
    return 0.14;
};
const round = (d) => d.toDecimalPlaces(0, Decimal.ROUND_HALF_EVEN).toNumber();
/**
 * Year-by-year accumulation projection. Contributions arrive evenly through the year (earnings/fees on half);
 * `returnRate` is net of investment tax; fees are a % of the average balance. Caps checked each year against
 * AU 2025-26 values (not indexed forward).
 */
export const superProjection = (p) => {
    for (const [k, v, min] of [
        ['returnRate', p.returnRate, -1], ['feeRate', p.feeRate, 0], ['inflation', p.inflation, -0.99], ['yearsToRetirement', p.yearsToRetirement, 0],
        ['sgRate', p.sgRate ?? 0, 0], ['contributionsTax', p.contributionsTax ?? 0, 0], ['salaryGrowthRate', p.salaryGrowthRate ?? 0, -1],
    ])
        requireFinite(k, v, min);
    if (p.yearsToRetirement > 100)
        throw new KeelError('VALIDATION', 'yearsToRetirement must be 100 or less');
    for (const [k, v] of [['balanceCents', p.balanceCents], ['salaryCents', p.salaryCents], ['extraConcessionalCents', p.extraConcessionalCents ?? 0],
        ['nonConcessionalCents', p.nonConcessionalCents ?? 0], ['insuranceCents', p.insuranceCents ?? 0]]) {
        if (!Number.isSafeInteger(v) || v < 0)
            throw new KeelError('VALIDATION', `${k} must be whole cents ≥ 0`, { [k]: v });
    }
    const sg = p.sgRate ?? AU_SUPER_CAPS.sgRate;
    const tax = p.contributionsTax ?? 0.15;
    const nonCon = p.nonConcessionalCents ?? 0;
    const years = [];
    const yearsOverConcessional = [];
    const warnings = [];
    let bal = p.balanceCents;
    let salary = new Decimal(p.salaryCents);
    const ncHistory = [];
    for (let y = 1; y <= Math.max(0, Math.floor(p.yearsToRetirement)); y++) {
        const concessional = round(salary.mul(sg)) + (p.extraConcessionalCents ?? 0);
        const contribTax = round(new Decimal(concessional).mul(tax));
        const net = concessional - contribTax + nonCon;
        const avg = new Decimal(bal).plus(new Decimal(net).div(2));
        const earnings = round(avg.mul(p.returnRate));
        const fees = round(avg.mul(p.feeRate));
        const insurance = p.insuranceCents ?? 0;
        if (concessional > AU_SUPER_CAPS.concessionalCents)
            yearsOverConcessional.push(y);
        if (nonCon > 0 && bal >= AU_SUPER_CAPS.transferBalanceCapCents) {
            warnings.push(`Year ${y}: your balance is at or above the transfer balance cap, so the non-concessional cap is nil.`);
        }
        ncHistory.push(nonCon);
        const window = ncHistory.slice(-3).reduce((a, b) => a + b, 0);
        if (window > AU_SUPER_CAPS.bringForwardCents)
            warnings.push(`Year ${y}: non-concessional contributions over three years exceed the bring-forward cap.`);
        const closing = bal + net + earnings - fees - insurance;
        years.push({
            year: y, openingCents: bal, concessionalCents: concessional, contributionsTaxCents: contribTax, nonConcessionalCents: nonCon,
            earningsCents: earnings, feesCents: fees, insuranceCents: insurance, closingCents: closing,
            realClosingCents: round(new Decimal(closing).div(new Decimal(1 + p.inflation).pow(y))),
        });
        bal = closing;
        salary = salary.mul(1 + (p.salaryGrowthRate ?? 0));
    }
    const firstConcessional = years[0]?.concessionalCents ?? round(new Decimal(p.salaryCents).mul(sg)) + (p.extraConcessionalCents ?? 0);
    if (yearsOverConcessional.length > 0)
        warnings.unshift(`Concessional contributions exceed the ${AU_SUPER_CAPS.financialYear} cap in ${yearsOverConcessional.length} year(s); excess is taxed at your marginal rate.`);
    if (nonCon > AU_SUPER_CAPS.nonConcessionalCents)
        warnings.push('Annual non-concessional contributions exceed the yearly cap; only possible using the bring-forward rule.');
    const last = years[years.length - 1];
    return {
        years,
        finalBalanceCents: last?.closingCents ?? p.balanceCents,
        finalRealCents: last?.realClosingCents ?? p.balanceCents,
        capsCheck: {
            concessionalCapCents: AU_SUPER_CAPS.concessionalCents, nonConcessionalCapCents: AU_SUPER_CAPS.nonConcessionalCents,
            bringForwardCapCents: AU_SUPER_CAPS.bringForwardCents, firstYearConcessionalCents: firstConcessional,
            concessionalExcessCents: Math.max(0, firstConcessional - AU_SUPER_CAPS.concessionalCents),
            nonConcessionalExcessCents: Math.max(0, nonCon - AU_SUPER_CAPS.nonConcessionalCents),
            yearsOverConcessional, warnings,
        },
    };
};
//# sourceMappingURL=super.js.map